/**
 * lSystemPipeline — 3D L-System recursive fractal tree.
 *
 * A real 3D raymarched L-system tree. We compute the distance to a
 * procedurally-generated fractal tree by SDF union of capsule branches.
 * Each branching level uses depth-of-recursion limited loops with
 * separate state variables (no recursion allowed in WebGL).
 *
 * Three trees at different positions, breathing with audio.
 * Sky gradient + leaves + ground plane.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface LSystemPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// 3D SDF for a capsule (line segment with rounded ends).
// Returns signed distance from p to the segment ab with radius r.
float sdCapsule(vec3 p, vec3 a, vec3 b, float r) {
  vec3 pa = p - a;
  vec3 ba = b - a;
  float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-4), 0.0, 1.0);
  return length(pa - ba * h) - r;
}

// Compute tree SDF at point p.
// The tree is unrolled to depth=5 with explicit branching.
// We use local variables and a single "best distance" accumulator.
float sdTree(vec3 p, vec3 origin, float seed) {
  // March up the tree by tracking current branch end.
  // For each level, compute two new branches at the tip and
  // accumulate the best (smallest) distance.
  float bestD = 1e6;
  // Capsule radius shrinks with depth (taper).
  float r0 = 0.04;
  // Initial branch.
  vec3 dir = normalize(vec3(
    (hash21(vec2(seed, 5.3)) - 0.5) * 0.4,
    0.7 + hash21(vec2(seed, 7.1)) * 0.4,
    (hash21(vec2(seed, 11.7)) - 0.5) * 0.4
  ));
  float len = 0.6;
  vec3 end = origin + dir * len;
  bestD = min(bestD, sdCapsule(p, origin, end, r0));
  vec3 curEnd = end;
  float curR = r0 * 0.7;
  float curLen = len * 0.7;
  float curSeed = seed + 1.0;

  // For each of 5 levels: spawn 2 sub-branches at curEnd, integrate
  // distances, and pick one of them as new "current".
  for (int level = 0; level < 5; level++) {
    float fl = float(level);
    vec3 axis = normalize(curEnd - vec3(0.0, 0.0, 0.0));  // growth axis
    // Compute two perpendicular axes for branch spread.
    vec3 up = abs(axis.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 right = normalize(cross(axis, up));
    vec3 forward = normalize(cross(right, axis));
    // Two random angles in the perpendicular plane.
    float ang1 = (hash21(vec2(curSeed + 1.3, 2.7)) * 2.0 - 1.0) * 0.6 + fl * 0.15;
    float ang2 = (hash21(vec2(curSeed + 2.7, 3.5)) * 2.0 - 1.0) * 0.6 - fl * 0.15;
    vec3 dir1 = normalize(axis + cos(ang1) * right * 0.7 + sin(ang1) * forward * 0.7);
    vec3 dir2 = normalize(axis + cos(ang2) * right * 0.7 + sin(ang2) * forward * 0.7);
    vec3 end1 = curEnd + dir1 * curLen;
    vec3 end2 = curEnd + dir2 * curLen;
    bestD = min(bestD, min(
      sdCapsule(p, curEnd, end1, curR),
      sdCapsule(p, curEnd, end2, curR)
    ));
    // Add leaf sphere at each tip.
    bestD = min(bestD, length(p - end1) - curR * 2.5);
    bestD = min(bestD, length(p - end2) - curR * 2.5);
    // Pick one as the new current.
    if (hash21(vec2(curSeed + 5.7, 7.7)) > 0.5) {
      curEnd = end1;
    } else {
      curEnd = end2;
    }
    curR = curR * 0.7;
    curLen = curLen * 0.75;
    curSeed = curSeed + 13.7;
  }
  return bestD;
}


// Sky gradient.
vec3 skyColor(vec3 rd, vec3 sunDir) {
  float t = clamp(rd.y * 0.5 + 0.5, 0.0, 1.0);
  vec3 horizon = vec3(0.95, 0.45, 0.30);
  vec3 zenith = vec3(0.04, 0.06, 0.30);
  vec3 col = mix(horizon, zenith, smoothstep(0.4, 1.0, t));
  float sun = max(dot(rd, sunDir), 0.0);
  col += vec3(1.0, 0.85, 0.55) * pow(sun, 32.0) * 1.5;
  col += vec3(1.0, 0.65, 0.40) * pow(sun, 4.0) * 0.3;
  return col;
}

// Single tree SDF (for picking which tree to draw).
float sdAllTrees(vec3 p) {
  return min(min(
    sdTree(p, vec3(-0.4, -0.3, -0.2), 1.7),
    sdTree(p, vec3( 0.5, -0.3,  0.3), 11.7)),
    sdTree(p, vec3( 0.0, -0.3, -0.5), 23.7)
  );
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;
  vec2 pNoAspect = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Camera.
  float ang = uTime * 0.10;
  vec3 ro = vec3(1.4 * cos(ang), 0.4, 1.4 * sin(ang));
  vec3 ta = vec3(0.0, 0.2, 0.0);
  vec3 cw = normalize(ta - ro);
  vec3 cu = normalize(cross(cw, vec3(0.0, 1.0, 0.0)));
  vec3 cv = cross(cu, cw);
  vec3 rd = normalize(p.x * cu + p.y * cv + 1.4 * cw);

  // Sky behind everything.
  vec3 col = skyColor(rd, normalize(vec3(0.5, 0.7, 0.5)));

  // Raymarch scene.
  float td = 0.0;
  bool hit = false;
  vec3 hitP;
  for (int i = 0; i < 96; i++) {
    vec3 pos = ro + rd * td;
    float d = sdAllTrees(pos);
    if (d < 0.001) {
      hit = true;
      hitP = pos;
      break;
    }
    td += max(d * 0.7, 0.005);
    if (td > 8.0) break;
  }

  if (hit) {
    // Normal estimation via gradient.
    vec2 e = vec2(0.005, 0.0);
    vec3 originA = vec3(-0.4, -0.3, -0.2);
    vec3 originB = vec3( 0.5, -0.3,  0.3);
    vec3 originC = vec3( 0.0, -0.3, -0.5);
    vec3 normal = normalize(vec3(
      sdAllTrees(hitP + e.xyy) - sdAllTrees(hitP - e.xyy) +
      sdAllTrees(hitP + e.xyy) - sdAllTrees(hitP - e.xyy),
      sdAllTrees(hitP + e.yxy) - sdAllTrees(hitP - e.yxy) +
      sdAllTrees(hitP + e.yxy) - sdAllTrees(hitP - e.yxy),
      sdAllTrees(hitP + e.yyx) - sdAllTrees(hitP - e.yyx) +
      sdAllTrees(hitP + e.yyx) - sdAllTrees(hitP - e.yyx)
    ));
    // Branches are dark wood; leaves (sphere bits near tips) are green
    // based on hitP distance from any tree origin.
    float distToOrigin = min(min(
      length(hitP - originA),
      length(hitP - originB)),
      length(hitP - originC));
    vec3 woodColor = vec3(0.35, 0.22, 0.15);
    vec3 leafColor = vec3(0.30, 0.65, 0.30);
    // The leaf spheres have varying radius; at the tips they are wide.
    // Use a heuristic: if the hit point is far from any capsule joint,
    // it's a leaf.
    float isLeaf = smoothstep(0.5, 1.2, length(hitP - vec3(0.0, hitP.y, 0.0)) * 0.0 + distToOrigin * 0.6);
    // Wait — simpler: at y > 0.1, more likely to be leaf.
    isLeaf = smoothstep(0.0, 0.3, hitP.y);
    vec3 baseColor = mix(woodColor, leafColor, isLeaf);
    // Lighting.
    vec3 lightDir = normalize(vec3(0.5, 1.0, 0.5));
    float diff = max(dot(normal, lightDir), 0.0);
    vec3 viewDir = -rd;
    vec3 halfDir = normalize(lightDir + viewDir);
    float spec = pow(max(dot(normal, halfDir), 0.0), 32.0);
    col = baseColor * (0.25 + 0.65 * diff);
    col += vec3(1.0) * spec * (isLeaf < 0.5 ? 0.3 : 0.1);
    col *= 1.0 + high * 0.3;
  }

  // Audio: pulse whole scene.
  col *= 1.0 + mid * 0.4;
  col = mix(col, col * vec3(1.2, 0.9, 1.0), bass * 0.3);

  // Vignette.
  float vig = 1.0 - dot(pNoAspect, pNoAspect) * 0.4;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function createLSystemPipeline(gl: WebGL2RenderingContext): LSystemPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    bands: new Float32Array(9),
  };
}

export function drawLSystem(
  gl: WebGL2RenderingContext,
  pipe: LSystemPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}

export function computeLSystemBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeLSystemRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}
